import React, { useEffect, useState, useMemo } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { useParams, Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import MpTopbar from "@/components/marketplace/MpTopbar";
import MpFooter from "@/components/marketplace/MpFooter";
import ReviewCard from "@/components/marketplace/ReviewCard";
import GMapEmbed from "@/components/marketplace/GMapEmbed";
import { Star, MapPin, Clock, Phone, Heart, Share2, ChevronRight, Check, X, ArrowRight, Plus, Minus, ShoppingBag, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { API_BASE } from "@/lib/api";
import "@/styles/marketplace.css";

const resolveMediaUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  const base = API_BASE.replace(/\/api$/, "");
  return base + u;
};

/**
 * Venue Detail page — /m/s/:slug
 *
 * Booking model:
 *   1. Branch selection is REQUIRED before services are shown / bookable.
 *   2. Customer adds multiple services to a cart (selectedServices[]).
 *   3. Single booking modal confirms the whole cart in one shot.
 */
export default function MarketplaceVenue() {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [venue, setVenue] = useState(null);
  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeBranchId, setActiveBranchId] = useState(null);
  const [tab, setTab] = useState("services");
  // Multi-service cart: keys are service_id/id.
  const [cart, setCart] = useState({});           // { [sid]: serviceObj }
  const [showCheckout, setShowCheckout] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const v = await api.get(`/marketplace/listings/${slug}`);
        if (!alive) return;
        setVenue(v.data);
        const [b, r] = await Promise.allSettled([
          api.get(`/marketplace/listings/${slug}/branches`),
          api.get(`/marketplace/listings/${slug}/reviews?status=approved`).catch(() => ({ data: { items: [] } })),
        ]);
        if (!alive) return;
        const branchList = b.status === "fulfilled" ? (b.value.data?.items || []) : [];
        setBranches(branchList);
        // Auto-select branch ONLY when exactly one is available; otherwise force the user to pick.
        if (branchList.length === 1) {
          setActiveBranchId(branchList[0].branch_id);
        } else {
          setActiveBranchId(null);
        }
        if (r.status === "fulfilled") setReviews(r.value.data?.items || []);
      } catch (e) {
        if (alive) setVenue(null);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [slug]);

  // Load services ONLY when a branch is selected (mandatory branch gate).
  useEffect(() => {
    if (!venue || !activeBranchId) { setServices([]); return; }
    let alive = true;
    (async () => {
      try {
        const params = new URLSearchParams({ branch_id: activeBranchId });
        const r = await api.get(`/marketplace/listings/${slug}/services?${params.toString()}`);
        if (alive) setServices(r.data?.items || []);
      } catch {
        if (alive) setServices([]);
      }
    })();
    return () => { alive = false; };
  }, [slug, venue, activeBranchId]);

  // Switching branch clears the cart — services may not be valid across branches.
  const onBranchChange = (bid) => {
    if (bid === activeBranchId) return;
    setActiveBranchId(bid);
    setCart({});
  };

  const toggleCart = (svc) => {
    const sid = svc.service_id || svc.id;
    setCart((prev) => {
      const next = { ...prev };
      if (next[sid]) delete next[sid]; else next[sid] = svc;
      return next;
    });
  };

  const cartList = Object.values(cart);
  const cartCount = cartList.length;
  const cartTotal = cartList.reduce((sum, s) => sum + (Number(s.price) || 0), 0);
  const cartDuration = cartList.reduce((sum, s) => sum + (Number(s.duration_minutes) || 0), 0);

  const branch = useMemo(() => branches.find(b => b.branch_id === activeBranchId), [branches, activeBranchId]);
  const venueName = venue?.name?.[isAr ? "ar" : "en"] || venue?.name?.en || "Salon";
  const venueAbout = venue?.about?.[isAr ? "ar" : "en"] || venue?.about?.en || "";

  if (loading) return (
    <div className="mp-root min-h-screen flex items-center justify-center" dir={isAr ? "rtl" : "ltr"}>
      <div className="text-[var(--mp-ink-muted)]">{isAr ? "جاري التحميل..." : "Loading..."}</div>
    </div>
  );

  if (!venue) return (
    <div className="mp-root" dir={isAr ? "rtl" : "ltr"}>
      <MpTopbar variant="solid" />
      <div className="mp-container py-24 text-center">
        <div className="text-5xl mb-4">😕</div>
        <h1 className="text-2xl font-bold">{isAr ? "الصفحة غير موجودة" : "Page not found"}</h1>
        <p className="text-[var(--mp-ink-muted)] mt-2">{isAr ? "ربما تم إخفاء هذا الصالون أو حُذف." : "This venue may have been hidden or removed."}</p>
        <Link to="/m" className="mp-btn mp-btn-primary mt-6 inline-flex">{isAr ? "العودة للماركت بليس" : "Back to marketplace"}</Link>
      </div>
      <MpFooter />
    </div>
  );

  const headerBranchLabel = (branch?.area_name && (branch.area_name[isAr ? "ar" : "en"])) || branch?.area_label || (branches[0]?.area_label);

  return (
    <div className="mp-root" dir={isAr ? "rtl" : "ltr"} data-testid="mp-venue">
      <MpTopbar variant="solid" />

      {/* Breadcrumb */}
      <div className="mp-container pt-6 pb-2 text-xs text-[var(--mp-ink-muted)] flex items-center gap-1.5">
        <Link to="/m" className="hover:text-[var(--mp-maroon-dark)]">{isAr ? "الماركت بليس" : "Marketplace"}</Link>
        <ChevronRight size={12} className={isAr ? "rotate-180" : ""} />
        <span className="text-[var(--mp-black)] font-medium truncate">{venueName}</span>
      </div>

      {/* Hero */}
      <section className="mp-container pt-4">
        <div className="relative rounded-2xl overflow-hidden bg-[var(--mp-cream)] aspect-[21/9] md:aspect-[21/7] lg:aspect-[21/6] shadow-md">
          {venue.media?.cover ? (
            <img src={resolveMediaUrl(venue.media.cover)} alt={venueName} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full mp-hero-gradient flex items-center justify-center text-7xl text-white/40">🪞</div>
          )}
          {venue.media?.logo && (
            <div className="absolute bottom-5 start-5 w-20 h-20 rounded-2xl overflow-hidden bg-white shadow-lg border-2 border-white" data-testid="mp-venue-logo">
              <img src={resolveMediaUrl(venue.media.logo)} alt="logo" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="absolute top-5 end-5 flex gap-2">
            <button className="w-11 h-11 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow-md hover:scale-105 transition"><Heart size={17} /></button>
            <button className="w-11 h-11 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow-md hover:scale-105 transition"><Share2 size={17} /></button>
          </div>
        </div>
      </section>

      {/* Header strip */}
      <section className="mp-container pt-8 pb-2">
        <div className="flex flex-col md:flex-row md:items-end gap-4 justify-between">
          <div className="space-y-3">
            <h1 className="text-4xl md:text-5xl font-bold text-[var(--mp-black)] leading-tight">{venueName}</h1>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[var(--mp-ink-muted)]">
              <span className="flex items-center gap-1.5 mp-stars">
                <Star size={15} fill="currentColor" stroke="none" />
                <span className="mp-stars-text text-base">{(venue.rating?.avg || 0).toFixed(1)}</span>
                <span className="ms-1">({venue.rating?.count || 0} {isAr ? "تقييم" : "reviews"})</span>
              </span>
              {headerBranchLabel && (
                <span className="flex items-center gap-1.5"><MapPin size={14} />{headerBranchLabel}</span>
              )}
              {branch?.phone && (
                <a href={`tel:${branch.phone}`} className="flex items-center gap-1.5 hover:text-[var(--mp-maroon-dark)]"><Phone size={14} />{branch.phone}</a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Branch selector — ALWAYS visible (required step) */}
      <section className="mp-container py-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-wider text-[var(--mp-ink-muted)] font-bold">
            {isAr
              ? `الخطوة 1: اختر الفرع ${branches.length > 0 ? `(${branches.length})` : ""}`
              : `Step 1: Choose a branch ${branches.length > 0 ? `(${branches.length})` : ""}`}
          </div>
          {activeBranchId && (
            <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
              <Check size={14} /> {isAr ? "تم الاختيار" : "Selected"}
            </div>
          )}
        </div>
        {branches.length === 0 ? (
          <div className="mp-card-flat p-6 text-sm text-[var(--mp-ink-muted)]">
            {isAr ? "لا توجد فروع متاحة لهذا الصالون على الماركت بليس." : "No branches available for this venue on the marketplace."}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="mp-branch-selector">
            {branches.map((b) => {
              const active = b.branch_id === activeBranchId;
              const areaLbl = (b.area_name && (b.area_name[isAr ? "ar" : "en"])) || b.area_label;
              return (
                <button
                  key={b.branch_id}
                  onClick={() => onBranchChange(b.branch_id)}
                  data-testid={`mp-branch-${b.branch_id}`}
                  className={`mp-chip ${active ? "mp-chip-active" : ""} !py-2.5 !px-4`}
                >
                  <MapPin size={14} />
                  <span className="font-semibold">{(b.name?.[isAr ? "ar" : "en"] || b.name?.en || b.branch_id)}</span>
                  {areaLbl && <span className="opacity-60 text-xs ms-1">· {areaLbl}</span>}
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Tabs + main content */}
      <section className="mp-container pb-16">
        <div className="flex flex-col lg:flex-row gap-10 mt-4">
          <div className="flex-1 min-w-0">
            {/* Tab strip */}
            <div className="border-b border-[var(--mp-border)] flex gap-8 mb-8 overflow-x-auto" data-testid="mp-tab-strip">
              {[
                { id: "services", label_ar: "الخدمات",     label_en: "Services", count: services.length },
                { id: "about",    label_ar: "عن الصالون",  label_en: "About" },
                { id: "photos",   label_ar: "الصور",       label_en: "Photos",   count: (venue.media?.gallery || []).length },
                { id: "reviews",  label_ar: "التقييمات",  label_en: "Reviews",  count: reviews.length },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`mp-tab-${t.id}`}
                  className={`pb-4 text-sm font-bold whitespace-nowrap border-b-2 transition ${tab === t.id ? "border-[var(--mp-black)] text-[var(--mp-black)]" : "border-transparent text-[var(--mp-ink-muted)] hover:text-[var(--mp-ink)]"}`}
                >
                  {isAr ? t.label_ar : t.label_en}
                  {t.count != null && <span className="ms-1.5 opacity-60">({t.count})</span>}
                </button>
              ))}
            </div>

            {tab === "services" && (
              <>
                <div className="text-xs uppercase tracking-wider text-[var(--mp-ink-muted)] font-bold mb-3">
                  {isAr ? "الخطوة 2: اختر خدمة أو أكثر" : "Step 2: Pick one or more services"}
                </div>

                {!activeBranchId ? (
                  <div className="mp-card-flat p-12 text-center" data-testid="mp-branch-required">
                    <AlertCircle size={40} className="mx-auto text-amber-500 mb-3" />
                    <div className="font-bold text-base">{isAr ? "اختر الفرع أولاً" : "Select a branch first"}</div>
                    <div className="text-sm text-[var(--mp-ink-muted)] mt-2">
                      {isAr ? "يجب اختيار الفرع قبل عرض الخدمات المتاحة." : "Branch selection is required to view available services."}
                    </div>
                  </div>
                ) : services.length === 0 ? (
                  <div className="mp-card-flat p-12 text-center">
                    <div className="text-4xl mb-3">💇</div>
                    <div className="font-bold text-base">{isAr ? "لم تُضَف خدمات بعد" : "No services listed yet"}</div>
                    <div className="text-sm text-[var(--mp-ink-muted)] mt-2">{isAr ? "تواصل مع الصالون مباشرة عبر الهاتف للحجز." : "Contact the venue directly to book."}</div>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="mp-services-list">
                    {services.map((s) => {
                      const sid = s.service_id || s.id;
                      const inCart = Boolean(cart[sid]);
                      const name = (isAr ? s.name_ar : s.name_en) || s.name_en || s.name_ar || "Service";
                      return (
                        <div
                          key={sid}
                          className={`bg-white border rounded-xl p-5 flex items-start justify-between gap-5 transition ${inCart ? "border-[var(--mp-maroon)] shadow-sm ring-2 ring-[var(--mp-maroon)]/15" : "border-[var(--mp-border)] hover:border-[var(--mp-maroon)] hover:shadow-sm"}`}
                          data-testid={`mp-service-${sid}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-[var(--mp-black)] text-base">{name}</div>
                            <div className="text-xs text-[var(--mp-ink-muted)] mt-1.5 flex items-center gap-3">
                              <span className="flex items-center gap-1"><Clock size={11} />{s.duration_minutes} {isAr ? "دقيقة" : "min"}</span>
                              {s.category && <span>· {s.category}</span>}
                            </div>
                            {s.description && <p className="text-xs text-[var(--mp-ink-muted)] mt-2 line-clamp-2 leading-relaxed">{s.description}</p>}
                          </div>
                          <div className="text-end shrink-0">
                            <div className="font-bold text-[var(--mp-black)] text-lg num-display">
                              {s.price || 0} <span className="text-xs font-normal text-[var(--mp-ink-muted)]">{venue.price_range?.currency || "QAR"}</span>
                            </div>
                            <button
                              onClick={() => toggleCart(s)}
                              data-testid={`mp-book-${sid}`}
                              className={`mp-btn !py-2 !px-4 !text-xs mt-3 inline-flex items-center gap-1 ${inCart ? "mp-btn-ghost" : "mp-btn-primary"}`}
                            >
                              {inCart
                                ? <><Minus size={12} />{isAr ? "إزالة" : "Remove"}</>
                                : <><Plus size={12} />{isAr ? "إضافة" : "Add"}</>
                              }
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {tab === "about" && (
              <div className="prose max-w-none space-y-6">
                <p className="text-[var(--mp-ink)] leading-relaxed whitespace-pre-line text-base">{venueAbout || (isAr ? "لم تُضَف معلومات بعد." : "No description yet.")}</p>
                {venue.amenities?.length > 0 && (
                  <div>
                    <h3 className="text-base font-bold mb-3">{isAr ? "المرافق" : "Amenities"}</h3>
                    <div className="flex flex-wrap gap-2">
                      {venue.amenities.map((a, i) => <span key={i} className="mp-chip">{a}</span>)}
                    </div>
                  </div>
                )}
                {Object.keys(branch?.working_hours || {}).length > 0 && (
                  <div>
                    <h3 className="text-base font-bold mb-3 flex items-center gap-2"><Clock size={15} />{isAr ? "ساعات العمل" : "Opening hours"}</h3>
                    <div className="space-y-1 text-sm">
                      {Object.entries(branch.working_hours).map(([day, h]) => (
                        <div key={day} className="flex justify-between max-w-sm py-1.5 border-b border-[var(--mp-divider)]">
                          <span className="text-[var(--mp-ink-muted)] capitalize">{day}</span>
                          <span>{h.closed ? (isAr ? "مغلق" : "Closed") : `${h.open} – ${h.close}`}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {tab === "photos" && (
              <div data-testid="mp-photos-tab">
                {(venue.media?.gallery || []).length === 0 ? (
                  <div className="mp-card-flat p-12 text-center">
                    <div className="text-4xl mb-3">📷</div>
                    <div className="font-bold">{isAr ? "لا توجد صور بعد" : "No photos yet"}</div>
                    <div className="text-sm text-[var(--mp-ink-muted)] mt-2">{isAr ? "سيقوم الصالون بإضافة صور قريباً." : "The venue will add photos soon."}</div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="mp-photos-grid">
                    {venue.media.gallery.map((url, i) => (
                      <a
                        key={`${url}-${i}`}
                        href={resolveMediaUrl(url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block aspect-square rounded-xl overflow-hidden bg-[var(--mp-cream)] group"
                        data-testid={`mp-photo-${i}`}
                      >
                        <img src={resolveMediaUrl(url)} alt={`${venueName} ${i+1}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === "reviews" && (
              <div className="space-y-4" data-testid="mp-reviews-tab">
                {reviews.length === 0 && (
                  <div className="mp-card-flat p-12 text-center">
                    <div className="text-4xl mb-3">⭐</div>
                    <div className="font-bold">{isAr ? "لا توجد تقييمات معتمدة بعد" : "No approved reviews yet"}</div>
                    <div className="text-sm text-[var(--mp-ink-muted)] mt-2">{isAr ? "كن أول من يشارك تجربته!" : "Be the first to share your experience!"}</div>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {reviews.map((r, i) => <ReviewCard key={r.review_id || i} review={r} />)}
                </div>
              </div>
            )}
          </div>

          {/* Sticky booking panel — cart summary */}
          <aside className="lg:w-[360px] shrink-0">
            <div className="mp-card-flat p-6 lg:sticky lg:top-[100px] space-y-4" data-testid="mp-cart-panel">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <ShoppingBag size={18} />
                  {isAr ? "سلة الحجز" : "Booking cart"}
                </h3>
                {cartCount > 0 && (
                  <span className="text-xs font-bold bg-[var(--mp-maroon)] text-white rounded-full px-2 py-0.5" data-testid="mp-cart-count">{cartCount}</span>
                )}
              </div>

              {!activeBranchId ? (
                <p className="text-sm text-[var(--mp-ink-muted)] leading-relaxed">
                  {isAr ? "اختر الفرع أولاً لبدء الحجز." : "Pick a branch first to start booking."}
                </p>
              ) : cartCount === 0 ? (
                <p className="text-sm text-[var(--mp-ink-muted)] leading-relaxed">
                  {isAr ? "أضف خدمة أو أكثر من القائمة لمتابعة الحجز." : "Add one or more services from the list to continue."}
                </p>
              ) : (
                <>
                  <ul className="space-y-2 max-h-[200px] overflow-y-auto -mx-1 px-1">
                    {cartList.map((s) => {
                      const sid = s.service_id || s.id;
                      const name = (isAr ? s.name_ar : s.name_en) || s.name_en || s.name_ar;
                      return (
                        <li key={sid} className="flex items-start justify-between gap-2 text-sm border-b border-[var(--mp-divider)] pb-2" data-testid={`mp-cart-item-${sid}`}>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">{name}</div>
                            <div className="text-xs text-[var(--mp-ink-muted)] flex items-center gap-1"><Clock size={10} />{s.duration_minutes} {isAr ? "د" : "min"}</div>
                          </div>
                          <div className="text-end shrink-0">
                            <div className="font-bold num-display">{s.price || 0}</div>
                            <button onClick={() => toggleCart(s)} className="text-xs text-[var(--mp-ink-muted)] hover:text-rose-600 mt-0.5" data-testid={`mp-cart-remove-${sid}`}>{isAr ? "حذف" : "Remove"}</button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="space-y-1.5 text-sm pt-1">
                    <div className="flex justify-between text-[var(--mp-ink-muted)]"><span>{isAr ? "إجمالي المدة" : "Total duration"}</span><span className="num-display">{cartDuration} {isAr ? "د" : "min"}</span></div>
                    <div className="flex justify-between font-bold text-base text-[var(--mp-black)]"><span>{isAr ? "المجموع" : "Total"}</span><span className="num-display">{cartTotal.toFixed(2)} {venue.price_range?.currency || "QAR"}</span></div>
                  </div>
                </>
              )}

              <button
                onClick={() => setShowCheckout(true)}
                disabled={!activeBranchId || cartCount === 0}
                data-testid="mp-cart-checkout"
                className="mp-btn mp-btn-primary w-full disabled:opacity-50"
              >
                {isAr ? "متابعة الحجز" : "Continue to checkout"} <ArrowRight size={14} className={isAr ? "rotate-180" : ""} />
              </button>

              {branch?.lat && branch?.lng && (
                <div className="pt-2">
                  <div className="text-xs font-bold text-[var(--mp-ink-muted)] uppercase tracking-wider mb-3">{isAr ? "الموقع" : "Location"}</div>
                  <GMapEmbed lat={branch.lat} lng={branch.lng} zoom={15} height="h-[200px]" testId="mp-venue-map" />
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>

      <MpFooter />

      {showCheckout && (
        <CheckoutModal
          slug={slug}
          services={cartList}
          branch={branch}
          venueName={venueName}
          currency={venue.price_range?.currency || "QAR"}
          totalPrice={cartTotal}
          totalDuration={cartDuration}
          onClose={() => { setShowCheckout(false); setCart({}); }}
          isAr={isAr}
        />
      )}
    </div>
  );
}

// ============================================================================
// CheckoutModal — multi-service booking confirmation (Date+Slot → Customer info)
// ============================================================================
function CheckoutModal({ slug, services, branch, venueName, currency, totalPrice, totalDuration, onClose, isAr }) {
  // Snapshot the cart at mount-time so the success view can render even after
  // the parent clears its cart on close.
  const [snapshotServices] = useState(services);
  const [snapshotTotalPrice] = useState(totalPrice);
  const [snapshotTotalDuration] = useState(totalDuration);
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [slot, setSlot] = useState("");
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoadingSlots(true);
    (async () => {
      try {
        const params = new URLSearchParams({ date });
        if (branch?.branch_id) params.set("branch_id", branch.branch_id);
        const r = await api.get(`/marketplace/listings/${slug}/slots?${params.toString()}`);
        if (alive) {
          setSlots(r.data?.slots || []);
          setSlot("");
        }
      } catch {
        if (alive) setSlots([]);
      } finally { if (alive) setLoadingSlots(false); }
    })();
    return () => { alive = false; };
  }, [slug, branch, date]);

  const onConfirm = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/marketplace/listings/${slug}/bookings`, {
        service_ids: snapshotServices.map(s => s.service_id || s.id),
        branch_id: branch.branch_id,
        date,
        time_slot: slot,
        customer_name: name,
        customer_phone: phone,
        notes,
      });
      setConfirmation(r.data?.confirmation);
      toast.success(isAr ? "تم تأكيد الحجز ✓" : "Booking confirmed ✓");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? "فشل الحجز" : "Booking failed"));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="mp-booking-modal" onMouseDown={(e) => onBackdropMouseDown(e, onClose)}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl" dir={isAr ? "rtl" : "ltr"} onMouseDown={onModalPanelMouseDown}>
        <div className="px-6 py-4 border-b flex items-start justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="font-bold text-[var(--mp-black)]">{isAr ? "حجز موعد" : "Book appointment"}</h2>
            <p className="text-xs text-[var(--mp-ink-muted)] mt-0.5">{venueName} · {snapshotServices.length} {isAr ? "خدمة" : snapshotServices.length === 1 ? "service" : "services"}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-stone-100 rounded" data-testid="mp-booking-close"><X size={18} /></button>
        </div>

        {confirmation ? (
          <div className="p-8 text-center space-y-4" data-testid="mp-booking-success">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto"><Check size={28} className="text-emerald-600" /></div>
            <h3 className="text-xl font-bold">{isAr ? "تم تأكيد حجزك!" : "Booking confirmed!"}</h3>
            <div className="text-sm text-[var(--mp-ink-muted)] space-y-1">
              <div><strong>{isAr ? "رقم الحجز:" : "Reference:"}</strong> <span className="font-mono">{(confirmation.booking_id || confirmation.id || "").slice(0, 12)}</span></div>
              <div>{date} · {slot}</div>
              <div className="font-semibold text-[var(--mp-black)] num-display">{confirmation.total_price?.toFixed?.(2) || snapshotTotalPrice.toFixed(2)} {currency}</div>
            </div>
            <button onClick={onClose} className="mp-btn mp-btn-primary w-full" data-testid="mp-booking-done">{isAr ? "تم" : "Done"}</button>
          </div>
        ) : (
          <>
            <div className="p-6 space-y-5">
              {/* Cart summary */}
              <div className="rounded-xl bg-[var(--mp-cream)] p-4 space-y-2" data-testid="mp-booking-cart-summary">
                <div className="text-xs font-bold uppercase tracking-wider text-[var(--mp-ink-muted)]">{isAr ? "ملخص الحجز" : "Order summary"}</div>
                <ul className="space-y-1.5">
                  {snapshotServices.map((s) => {
                    const sid = s.service_id || s.id;
                    const nm = (isAr ? s.name_ar : s.name_en) || s.name_en || s.name_ar;
                    return (
                      <li key={sid} className="flex items-center justify-between text-sm" data-testid={`mp-summary-${sid}`}>
                        <span className="truncate flex-1 me-3">{nm}</span>
                        <span className="num-display font-semibold shrink-0">{s.price || 0} {currency}</span>
                      </li>
                    );
                  })}
                </ul>
                <div className="border-t border-[var(--mp-divider)] pt-2 flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1 text-[var(--mp-ink-muted)]"><Clock size={12} />{snapshotTotalDuration} {isAr ? "د" : "min"}</span>
                  <span className="font-bold num-display">{snapshotTotalPrice.toFixed(2)} {currency}</span>
                </div>
              </div>

              {/* Date */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[var(--mp-ink-muted)] block mb-2">{isAr ? "التاريخ" : "Date"}</label>
                <input type="date" value={date} min={today} onChange={(e) => setDate(e.target.value)} data-testid="mp-booking-date" className="mp-input" />
              </div>

              {/* Slots */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[var(--mp-ink-muted)] block mb-2">{isAr ? "الوقت المتاح" : "Available slots"}</label>
                {loadingSlots ? (
                  <div className="text-sm text-[var(--mp-ink-muted)] py-4 text-center">{isAr ? "جاري التحميل..." : "Loading..."}</div>
                ) : slots.length === 0 ? (
                  <div className="text-sm text-rose-600 py-4 text-center bg-rose-50 rounded-lg">{isAr ? "لا توجد أوقات متاحة بهذا التاريخ" : "No slots on this date"}</div>
                ) : (
                  <div className="grid grid-cols-4 gap-2" data-testid="mp-booking-slots">
                    {slots.map(s => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setSlot(s)}
                        data-testid={`mp-slot-${s}`}
                        className={`py-2 rounded-lg border text-sm font-semibold transition ${slot === s ? "bg-[var(--mp-black)] text-white border-[var(--mp-black)]" : "bg-white border-[var(--mp-border)] hover:border-[var(--mp-black)]"}`}
                      >{s}</button>
                    ))}
                  </div>
                )}
              </div>

              {/* Customer info — only after slot selected */}
              {slot && (
                <div className="space-y-3 pt-2 border-t">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-[var(--mp-ink-muted)] block mb-2">{isAr ? "الاسم" : "Your name"}</label>
                    <input value={name} onChange={(e) => setName(e.target.value)} data-testid="mp-booking-name" className="mp-input" placeholder={isAr ? "الاسم الكامل" : "Full name"} />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-[var(--mp-ink-muted)] block mb-2">{isAr ? "رقم الجوال" : "Phone"}</label>
                    <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="mp-booking-phone" className="mp-input" placeholder="+974..." />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-[var(--mp-ink-muted)] block mb-2">{isAr ? "ملاحظات (اختياري)" : "Notes (optional)"}</label>
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="mp-booking-notes" rows={2} className="mp-input" />
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t sticky bottom-0 bg-white">
              <button
                onClick={onConfirm}
                disabled={!date || !slot || !name || !phone || saving}
                data-testid="mp-booking-confirm"
                className="mp-btn mp-btn-primary w-full !py-3.5 disabled:opacity-50"
              >
                {saving ? (isAr ? "جاري التأكيد..." : "Confirming...") : (isAr ? `تأكيد الحجز · ${snapshotTotalPrice.toFixed(2)} ${currency}` : `Confirm booking · ${snapshotTotalPrice.toFixed(2)} ${currency}`)}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
